import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, noop } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { Account } from '../models/account';
import { Common } from '../models/common';
import { FieldCrops } from '../models/field-crops';
import { FieldProducers } from '../models/field-producers';
import { Fields } from '../models/fields';
import { AdvisorsService } from '../services/advisors.service';
import { AreasService } from '../services/areas.service';
import { CampaignsService } from '../services/campaigns.service';
import { ClassificationsService } from '../services/classifications.service';
import { CropsService } from '../services/crops.service';
import { FieldCropsService } from '../services/field-crops.service';
import { FieldProducersService } from '../services/field-producers.service';
import { FieldsService } from '../services/fields.service';
import { MapsService } from '../services/maps.service';
import { ProducersService } from '../services/producers.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { DownloadFilesService } from '../services/download-files.service';
import { SpinnerService } from '../services/spinner.service';

@Component({
  selector: 'app-field',
  templateUrl: './field.component.html',
  styleUrls: ['./field.component.css']
})
export class FieldComponent implements OnInit {
  @Input() polygon: any;
  @Output() closeAction = new EventEmitter<any>();
  modalRef: BsModalRef | null;
  saved = false;

  isLoading = false;
  isSaving = false;
  isSubmitting = false;
  model: Fields = new Fields();
  campaigns: Common[];
  crops: Common[];
  classifications: Common[];
  areas: Common[];
  advisors: Common[];
  producersResults: any[];
  selectedProducer: Account;

  fieldCrops = [];

  constructor(private mapService: MapsService, private campaignsService: CampaignsService, private fieldService: FieldsService,
    private classificationsService: ClassificationsService, private cropsService: CropsService, private areasService: AreasService,
    private fieldCropsService: FieldCropsService, private advisorsService: AdvisorsService, private toastr: ToastrService,
    private producersService: ProducersService, private fieldProducersService: FieldProducersService,
    private modalService: BsModalService, private downloadFilesService: DownloadFilesService,
    private spinnerService: SpinnerService) {

    this.campaignsService.getCombo().subscribe(data => {
      this.campaigns = data;
      this.model.campaignId = data[0].id;
    });

    this.classificationsService.getCombo().subscribe(data => {
      this.classifications = data;
    });

    this.cropsService.getCombo().subscribe(data => {
      this.crops = data;
    });

    this.areasService.getCombo().subscribe(data => {
      this.areas = data;
    });

    this.advisorsService.getCombo().subscribe(data => {
      this.advisors = data;
    });
  }

  ngOnInit() {
    if (this.polygon.id !== undefined) {
      this.isLoading = true;
      this.spinnerService.setTrue();
      this.mapService.getField(this.polygon.id).pipe(
        switchMap((data: Fields) => {
          this.model = data;
          return forkJoin([
            this.fieldCropsService.getList(data.campaignId, data.id),
            this.fieldProducersService.get(data.campaignId, data.id)
          ]);
        }),
        finalize(() => {
          this.isLoading = false;
          this.spinnerService.setFalse();
        })
      ).subscribe(([fieldCrops, producer]) => {
        this.fieldCrops = fieldCrops;
        this.setSelectedProducer(producer);
      }, (err: HttpErrorResponse) => {
        this.fieldService.handleError(err, this.toastr);
      });
    } else {
      this.model.hectares = this.polygon.hectares;
    }
  }

  refreshFieldCrops(campaignId, fieldId) {
    if (this.model.id > 0) {
      this.fieldCropsService.getList(campaignId, fieldId).subscribe(result => {
        this.fieldCrops = result;
      });
    }
  }

  searchProducers(event) {
    this.producersService.get(event.query).subscribe(data => {
      this.producersResults = data;
    });
  }

  selectProducer(event) {
    if (event === null || event === undefined) { return; }
    this.selectedProducer = event;
    const producer = new FieldProducers();
    producer.accountNumber = event.nroCuenta;
    // Search results show '<account> - <name>'; store only the name, as before
    const accountPrefix = `${event.nroCuenta} - `;
    producer.accountName = event.nombre.startsWith(accountPrefix) ? event.nombre.substring(accountPrefix.length) : event.nombre;
    producer.campaignId = this.model.campaignId;
    producer.fieldId = this.model.id;
    this.fieldProducersService.save(producer).subscribe(noop);
  }

  refreshProducer() {
    this.fieldProducersService.get(this.model.campaignId, this.model.id).subscribe(result => {
      this.setSelectedProducer(result);
    });
  }

  setSelectedProducer(fieldProducer: any) {
    this.selectedProducer = fieldProducer === null ? undefined : new Account(fieldProducer.accountNumber, fieldProducer.accountName);
  }

  deleteProducer(event) {
    this.fieldProducersService.delete(this.model.campaignId, this.model.id).subscribe(result => {
      this.selectedProducer = undefined;
    });
  }

  onChangeCampaing() {
    this.refreshProducer();
    this.refreshFieldCrops(this.model.campaignId, this.model.id);
  }

  clearProducer(event) {
    this.selectedProducer = undefined;
  }

  onCancel() {
    this.closeAction.emit({ closePopup: true, cancelAction: !this.saved, id: this.model.id });
  }

  onSubmit() {
    this.saved = true;
    this.model.coordinates = this.polygon.getPath().getArray();

    this.isSubmitting = true;
    this.spinnerService.setTrue();
    this.fieldService.save(this.model, this.polygon.id).pipe(
      finalize(() => {
        this.isSubmitting = false;
        this.spinnerService.setFalse();
      })
    ).subscribe(result => {
      if (this.polygon.id === undefined) {
        this.polygon.id = result;
      }

      if (this.model.id === undefined) {
        this.model.id = result;
      }
      this.toastr.success('Grabación exitosa', 'Éxito');
      this.closeAction.emit({ closePopup: false, cancelAction: false, classificationId: this.model.classificationId });
    }, (err: HttpErrorResponse) => {
      this.fieldService.handleError(err, this.toastr);
    });
  }

  saveFieldCrop() {
    if (this.model.cropId === null || this.model.cropId === undefined || Number(this.model.cropId) <= 0) {
      this.toastr.warning('Ingrese el cultivo', 'Advertencia');
      return;
    }

    if (this.model.cropHectare === null || this.model.cropHectare === undefined || Number(this.model.cropHectare) <= 0) {
      this.toastr.warning('Ingrese las hectáreas', 'Advertencia');
      return;
    }

    const fc = new FieldCrops();
    fc.cropId = this.model.cropId;
    fc.fieldId = this.model.id;
    fc.campaignId = this.model.campaignId;
    fc.hectares = this.model.cropHectare;
    if (this.model.id !== undefined) {
      this.isSaving = true;
      this.fieldCropsService.save(fc).subscribe(result => {
        this.toastr.success('Grabación exitosa', 'Éxito');
        this.model.cropId = null;
        this.model.cropHectare = null;
        this.refreshFieldCrops(this.model.campaignId, this.model.id);
        this.isSaving = false;
      }, (err: HttpErrorResponse) => {
        this.isSaving = false;
        this.fieldCropsService.handleError(err, this.toastr);
      });
    }
  }

  deleteFieldCrop(fieldCrop: FieldCrops) {
    this.fieldCropsService.delete(fieldCrop.campaignId, this.model.id, fieldCrop.cropId).subscribe(result => {
      this.toastr.success('Eliminación exitosa', 'Éxito');
      this.model.cropId = null;
      this.model.cropHectare = null;

      this.refreshFieldCrops(this.model.campaignId, this.model.id);
    });
  }

  onDelete(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template, { class: 'modal-sm' });
  }

  confirm(): void {
    this.fieldService.delete(this.model.id).subscribe(result => {
      this.polygon.id = undefined;
      this.toastr.success('Eliminación exitosa', 'Éxito');
      this.modalRef.hide();
      this.closeAction.emit({ closePopup: true, cancelAction: true });
    }, (err: HttpErrorResponse) => {
      this.fieldService.handleError(err, this.toastr);
      this.modalRef.hide();
    });
  }

  decline(): void {
    this.modalRef.hide();
  }

  onExportPolygon() {
    this.downloadFilesService.downloadPolygonKMZ(this.model.id, this.model.name);
  }
}
